// src/pages/DentistRegistration.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
  HStack,
  useToast,
  Select,
  Checkbox,
  Link
} from '@chakra-ui/react';
import API from '../api/axiosConfig';

const DentistRegistration = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Practice Information
    practiceName: '',
    practiceEmail: '',
    practicePhone: '',
    practiceWebsite: '',
    
    // Address
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Contact Person
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    
    // Login Credentials
    password: '',
    confirmPassword: '',
    
    // Open Dental Info
    openDentalVersion: '',
    
    // Agreement
    agreeToTerms: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    // Practice Name
    if (!formData.practiceName.trim()) {
      toast({
        title: 'Practice Name Required',
        description: 'Please enter your practice name.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.practiceEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid practice email address.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Contact person
    if (!formData.contactFirstName.trim() || !formData.contactLastName.trim()) {
      toast({
        title: 'Contact Information Required',
        description: 'Please enter contact person name.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Password validation
    if (formData.password.length < 8) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 8 characters.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Passwords Do Not Match',
        description: 'Please make sure your passwords match.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Terms agreement
    if (!formData.agreeToTerms) {
      toast({
        title: 'Terms Agreement Required',
        description: 'Please agree to the terms and conditions.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Format data for backend
      const registrationPayload = {
        practiceName: formData.practiceName,
        email: formData.practiceEmail,
        phone: formData.practicePhone,
        website: formData.practiceWebsite || '',
        address: {
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        },
        contactPerson: {
          firstName: formData.contactFirstName,
          lastName: formData.contactLastName,
          email: formData.contactEmail,
          phone: formData.contactPhone
        },
        password: formData.password,
        openDentalVersion: formData.openDentalVersion || ''
      };

      // API call to register dental practice
      await API.post('/practice/register', registrationPayload);

      toast({
        title: 'Registration Successful!',
        description: 'Thank you for registering! We\'ll contact you within 24 hours to get you set up.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      // Redirect to homepage after success
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.message || 'Unable to submit registration. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(135deg, #f8fbff 0%, #e3f2fd 100%)"
      py={12}
    >
      <Container maxW="container.md">
        {/* Logo Link */}
        <Flex justify="center" mb={6}>
          <Box
            onClick={() => navigate('/')}
            cursor="pointer"
            transition="all 0.3s ease"
            _hover={{ transform: 'scale(1.05)' }}
          >
            <Text
              fontSize="2.5rem"
              fontWeight="900"
              letterSpacing="-2px"
              lineHeight="1"
            >
              <Box
                as="span"
                style={{
                  background: "linear-gradient(135deg, #1e3c72 0%, #4a90e2 100%)",
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Book
              </Box>
              <Box as="span" color="#1e3c72">
                Dentist
              </Box>
              <Box
                as="span"
                style={{
                  background: "linear-gradient(135deg, #1e3c72 0%, #4a90e2 100%)",
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                .pro
              </Box>
            </Text>
          </Box>
        </Flex>

        {/* Header */}
        <VStack spacing={2} mb={8} textAlign="center">
          <Heading
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight="900"
            bgGradient="linear(135deg, #1e3c72 0%, #4a90e2 100%)"
            bgClip="text"
          >
            Start Your 14-Day Free Trial
          </Heading>
          <Text color="#5a7a95" fontSize="lg" fontWeight="500">
            Join 2,500+ dental practices using BookDentist.pro
          </Text>
          <Text color="#5a7a95" fontSize="sm">
            No credit card required • Cancel anytime
          </Text>
        </VStack>

        {/* Registration Form */}
        <Box
          bg="white"
          p={{ base: 8, md: 10 }}
          borderRadius="25px"
          boxShadow="0 25px 80px rgba(0, 0, 0, 0.1)"
          border="2px solid"
          borderColor="rgba(74, 144, 226, 0.1)"
        >
          <form onSubmit={handleSubmit}>
            <VStack spacing={8} align="stretch">
              {/* Practice Information Section */}
              <Box>
                <Heading size="md" color="#1e3c72" mb={4}>
                  Practice Information
                </Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="#1e3c72" fontWeight="600">Practice Name</FormLabel>
                    <Input
                      value={formData.practiceName}
                      onChange={(e) => handleInputChange('practiceName', e.target.value)}
                      placeholder="Smile Dental Care"
                      size="lg"
                      borderColor="rgba(74, 144, 226, 0.2)"
                      _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                    />
                  </FormControl>

                  <HStack spacing={4} w="full" flexDirection={{ base: 'column', md: 'row' }}>
                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">Practice Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.practiceEmail}
                        onChange={(e) => handleInputChange('practiceEmail', e.target.value)}
                        placeholder="contact@yourpractice.com"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">Practice Phone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.practicePhone}
                        onChange={(e) => handleInputChange('practicePhone', e.target.value)}
                        placeholder="(555) 123-4567"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel color="#1e3c72" fontWeight="600">Practice Website (Optional)</FormLabel>
                    <Input
                      value={formData.practiceWebsite}
                      onChange={(e) => handleInputChange('practiceWebsite', e.target.value)}
                      placeholder="www.yourpractice.com"
                      size="lg"
                      borderColor="rgba(74, 144, 226, 0.2)"
                      _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Address Section */}
              <Box>
                <Heading size="md" color="#1e3c72" mb={4}>
                  Practice Address
                </Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="#1e3c72" fontWeight="600">Street Address</FormLabel>
                    <Input
                      value={formData.streetAddress}
                      onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                      placeholder="123 Main Street"
                      size="lg"
                      borderColor="rgba(74, 144, 226, 0.2)"
                      _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                    />
                  </FormControl>

                  <HStack spacing={4} w="full" flexDirection={{ base: 'column', md: 'row' }}>
                    <FormControl isRequired flex={2}>
                      <FormLabel color="#1e3c72" fontWeight="600">City</FormLabel>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Jupiter"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>

                    <FormControl isRequired flex={1}>
                      <FormLabel color="#1e3c72" fontWeight="600">State</FormLabel>
                      <Select
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="Select"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      >
                        <option value="AL">AL</option>
                        <option value="AK">AK</option>
                        <option value="AZ">AZ</option>
                        <option value="AR">AR</option>
                        <option value="CA">CA</option>
                        <option value="CO">CO</option>
                        <option value="CT">CT</option>
                        <option value="DE">DE</option>
                        <option value="FL">FL</option>
                        <option value="GA">GA</option>
                        <option value="HI">HI</option>
                        <option value="ID">ID</option>
                        <option value="IL">IL</option>
                        <option value="IN">IN</option>
                        <option value="IA">IA</option>
                        <option value="KS">KS</option>
                        <option value="KY">KY</option>
                        <option value="LA">LA</option>
                        <option value="ME">ME</option>
                        <option value="MD">MD</option>
                        <option value="MA">MA</option>
                        <option value="MI">MI</option>
                        <option value="MN">MN</option>
                        <option value="MS">MS</option>
                        <option value="MO">MO</option>
                        <option value="MT">MT</option>
                        <option value="NE">NE</option>
                        <option value="NV">NV</option>
                        <option value="NH">NH</option>
                        <option value="NJ">NJ</option>
                        <option value="NM">NM</option>
                        <option value="NY">NY</option>
                        <option value="NC">NC</option>
                        <option value="ND">ND</option>
                        <option value="OH">OH</option>
                        <option value="OK">OK</option>
                        <option value="OR">OR</option>
                        <option value="PA">PA</option>
                        <option value="RI">RI</option>
                        <option value="SC">SC</option>
                        <option value="SD">SD</option>
                        <option value="TN">TN</option>
                        <option value="TX">TX</option>
                        <option value="UT">UT</option>
                        <option value="VT">VT</option>
                        <option value="VA">VA</option>
                        <option value="WA">WA</option>
                        <option value="WV">WV</option>
                        <option value="WI">WI</option>
                        <option value="WY">WY</option>
                      </Select>
                    </FormControl>

                    <FormControl isRequired flex={1}>
                      <FormLabel color="#1e3c72" fontWeight="600">ZIP Code</FormLabel>
                      <Input
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        placeholder="33458"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Contact Person Section */}
              <Box>
                <Heading size="md" color="#1e3c72" mb={4}>
                  Primary Contact
                </Heading>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full" flexDirection={{ base: 'column', md: 'row' }}>
                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">First Name</FormLabel>
                      <Input
                        value={formData.contactFirstName}
                        onChange={(e) => handleInputChange('contactFirstName', e.target.value)}
                        placeholder="John"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">Last Name</FormLabel>
                      <Input
                        value={formData.contactLastName}
                        onChange={(e) => handleInputChange('contactLastName', e.target.value)}
                        placeholder="Smith"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full" flexDirection={{ base: 'column', md: 'row' }}>
                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">Contact Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                        placeholder="john@yourpractice.com"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="#1e3c72" fontWeight="600">Contact Phone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                        placeholder="(555) 123-4567"
                        size="lg"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Open Dental Info */}
              <Box>
                <Heading size="md" color="#1e3c72" mb={4}>
                  Open Dental Information
                </Heading>
                <FormControl>
                  <FormLabel color="#1e3c72" fontWeight="600">Open Dental Version (Optional)</FormLabel>
                  <Input
                    value={formData.openDentalVersion}
                    onChange={(e) => handleInputChange('openDentalVersion', e.target.value)}
                    placeholder="e.g., 22.3"
                    size="lg"
                    borderColor="rgba(74, 144, 226, 0.2)"
                    _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                  />
                  <Text fontSize="sm" color="#5a7a95" mt={2}>
                    Don't know your version? That's okay, we'll help you find it during setup.
                  </Text>
                </FormControl>
              </Box>

              {/* Password Section */}
              <Box>
                <Heading size="md" color="#1e3c72" mb={4}>
                  Create Password
                </Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="#1e3c72" fontWeight="600">Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Minimum 8 characters"
                      size="lg"
                      borderColor="rgba(74, 144, 226, 0.2)"
                      _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color="#1e3c72" fontWeight="600">Confirm Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Re-enter your password"
                      size="lg"
                      borderColor="rgba(74, 144, 226, 0.2)"
                      _focus={{ borderColor: "#4a90e2", boxShadow: "0 0 0 1px #4a90e2" }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Terms Agreement */}
              <FormControl isRequired>
                <Checkbox
                  isChecked={formData.agreeToTerms}
                  onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                  colorScheme="blue"
                  size="lg"
                >
                  <Text color="#1e3c72" fontSize="sm">
                    I agree to the{' '}
                    <Link color="#4a90e2" fontWeight="600" href="/terms" isExternal>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link color="#4a90e2" fontWeight="600" href="/privacy" isExternal>
                      Privacy Policy
                    </Link>
                  </Text>
                </Checkbox>
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                isLoading={isLoading}
                loadingText="Submitting..."
                bgGradient="linear(135deg, #4a90e2 0%, #74b9ff 100%)"
                color="white"
                size="lg"
                h="60px"
                fontSize="lg"
                fontWeight="800"
                borderRadius="15px"
                boxShadow="0 8px 30px rgba(74, 144, 226, 0.4)"
                _hover={{
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 40px rgba(74, 144, 226, 0.6)"
                }}
                transition="all 0.3s ease"
              >
                Start 14-Day Free Trial
              </Button>

              <Text textAlign="center" color="#5a7a95" fontSize="sm">
                Already have an account?{' '}
                <Link color="#4a90e2" fontWeight="600" onClick={() => navigate('/')}>
                  Sign In
                </Link>
              </Text>
            </VStack>
          </form>
        </Box>

        {/* Trust Indicators */}
        <Flex
          justify="center"
          gap={8}
          mt={8}
          flexWrap="wrap"
          color="#5a7a95"
          fontSize="sm"
          fontWeight="600"
        >
          <Flex align="center" gap={2}>
            <Text fontSize="lg">✓</Text>
            <Text>14-day trial</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="lg">✓</Text>
            <Text>No credit card</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="lg">✓</Text>
            <Text>HIPAA compliant</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="lg">✓</Text>
            <Text>Cancel anytime</Text>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

export default DentistRegistration;